import { ApiError, type Client, type UploadTicket } from "@khazzin/api-client";
import type { OutboxEntry, OutboxStore } from "@khazzin/api-client/sync";
import { all, del, get, put } from "./db";

/**
 * Photos and signatures waiting on the phone (EP10-S17, EP10-S18): taken with
 * no signal, kept in the `media` store, and named `media:<id>` by the queued
 * command that carries them — a failed attempt's `proofRef`, a POD's
 * `photoBlobRefs` and `signatureBlobRef`.
 *
 * **Outcome first, media retries on its own** (`10/03` §9, `10/04` §3). Each
 * sync cycle, before the push, {@link reserveMedia} asks the server for an
 * attachment id per file a queued command names — a small call, no bytes —
 * and rewrites the command to carry it. The push then lands the delivery and
 * its POD straight away; the bytes follow in {@link uploadMedia}, after the
 * push, one file at a time with its own retry. Until a file lands, the POD
 * reads `blobsPending` — the shipment's `pod_pending` — and the console says
 * the file is uploading. A form that expired while the phone had no signal is
 * renewed for the same attachment (`POST /attachments/{id}/upload-form`).
 */

export type MediaKind = "photo" | "signature";

/** A file on the phone. Bytes, not a Blob: a Blob does not survive every IndexedDB. */
export interface StoredMedia {
	bytes: ArrayBuffer;
	type: string;
	shipmentId: string;
	/** Absent on a photo kept by an EP10-S17 build: a photo. */
	kind?: MediaKind;
	/** Set once reserved: the attachment the queued command now names. */
	id?: string;
	attachmentId?: string;
	form?: UploadTicket["upload"];
	/** The bytes were posted; only the server's check is left. */
	sent?: boolean;
}

export const MEDIA_REF = "media:";

/** The attachment purposes (EP03-S17), by kind. */
const PURPOSE: Record<MediaKind, string> = { photo: "pod_photo", signature: "pod_signature" };

/** A form this close to its expiry is renewed rather than raced. */
const FORM_MARGIN_MS = 60_000;

export async function keepPhoto(
	id: string,
	shipmentId: string,
	photo: Blob,
	kind: MediaKind = "photo",
): Promise<void> {
	const stored: StoredMedia = {
		bytes: await photo.arrayBuffer(),
		type: photo.type || (kind === "signature" ? "image/png" : "image/jpeg"),
		shipmentId,
		kind,
	};
	await put("media", id, stored);
}

export async function photoOf(id: string): Promise<Blob | null> {
	const stored = await get<StoredMedia>("media", id);
	return stored === undefined ? null : new Blob([stored.bytes], { type: stored.type });
}

export function dropPhoto(id: string): Promise<void> {
	return del("media", id);
}

/** The signed form upload: the file goes straight to storage. */
export type SendForm = (url: string, form: FormData) => Promise<boolean>;

const sendForm: SendForm = async (url, form) =>
	(await fetch(url, { method: "POST", body: form })).ok;

/** The payload fields that may name a file on the phone. */
const REF_FIELDS = ["proofRef", "photoBlobRefs", "signatureBlobRef"] as const;

function localId(ref: unknown): string | null {
	return typeof ref === "string" && ref.startsWith(MEDIA_REF) ? ref.slice(MEDIA_REF.length) : null;
}

function refsOf(entry: OutboxEntry): string[] {
	return REF_FIELDS.flatMap((field) => {
		const value = entry.payload[field];
		return (Array.isArray(value) ? value : [value]).flatMap((ref) => localId(ref) ?? []);
	});
}

function extension(type: string): string {
	return type === "image/png" ? "png" : type === "image/webp" ? "webp" : "jpg";
}

/**
 * Gives every file a queued command names an attachment id, in capture
 * order, and rewrites the command to carry it. A file already reserved (a
 * cycle that stopped half way) keeps its id. A file the phone lost leaves
 * the command without it: the server then decides whether the proof still
 * holds, and a POD it refuses is held for dispatch like any conflict. A
 * failure rejects, and the cycle backs off with the command untouched.
 */
export async function reserveMedia(
	api: Pick<Client, "beginUpload">,
	outbox: OutboxStore,
): Promise<void> {
	const waiting = (await outbox.all())
		.filter((e) => e.state === "queued" && refsOf(e).length > 0)
		.sort((a, b) => a.seq - b.seq);
	for (const entry of waiting) {
		const ids = new Map<string, string | null>();
		for (const local of refsOf(entry)) {
			const stored = await get<StoredMedia>("media", local);
			if (stored === undefined) {
				ids.set(local, null);
				continue;
			}
			if (stored.attachmentId === undefined) {
				const kind = stored.kind ?? "photo";
				const begun = await api.beginUpload({
					purpose: PURPOSE[kind],
					ownerId: stored.shipmentId,
					filename: `${local}.${extension(stored.type)}`,
					contentType: stored.type,
					sizeBytes: stored.bytes.byteLength,
				});
				stored.id = local;
				stored.attachmentId = begun.attachment.id;
				stored.form = begun.upload;
				await put("media", local, stored);
			}
			ids.set(local, stored.attachmentId);
		}
		const swap = (ref: unknown) => {
			const local = localId(ref);
			return local === null ? ref : ids.get(local);
		};
		const payload: Record<string, unknown> = { ...entry.payload };
		for (const field of REF_FIELDS) {
			const value = payload[field];
			if (Array.isArray(value)) {
				payload[field] = value.map(swap).filter((ref) => ref != null);
			} else if (localId(value) !== null) {
				const next = swap(value);
				if (next == null) {
					delete payload[field];
				} else {
					payload[field] = next;
				}
			}
		}
		await outbox.put({ ...entry, payload });
	}
}

/**
 * Posts the bytes of every reserved file and asks the server to check them,
 * each on its own: one that fails stays for the next cycle and the others
 * still go. A form about to expire is renewed; a file the server already has
 * (`409 attachment_ready`) leaves the phone. Resolves true when nothing is
 * left to upload, false when something must be retried.
 */
export async function uploadMedia(
	api: Pick<Client, "renewUpload" | "completeUpload">,
	send: SendForm = sendForm,
	now: () => number = Date.now,
): Promise<boolean> {
	const reserved = (await all<StoredMedia>("media")).filter(
		(m) => m.attachmentId !== undefined && m.id !== undefined,
	);
	let done = true;
	for (const media of reserved) {
		const id = media.id ?? "";
		const attachmentId = media.attachmentId ?? "";
		try {
			if (media.sent !== true) {
				let form = media.form;
				// Negated so an unreadable expiry counts as expired.
				if (form === undefined || !(Date.parse(form.expiresAt) - now() >= FORM_MARGIN_MS)) {
					form = (await api.renewUpload(attachmentId)).upload;
					media.form = form;
					await put("media", id, media);
				}
				const body = new FormData();
				for (const [key, value] of Object.entries(form.fields)) {
					body.append(key, value);
				}
				body.append(
					"file",
					new Blob([media.bytes], { type: media.type }),
					`${id}.${extension(media.type)}`,
				);
				if (!(await send(form.url, body))) {
					throw new Error("the upload was refused");
				}
				media.sent = true;
				await put("media", id, media);
			}
			try {
				await api.completeUpload(attachmentId);
			} catch (error) {
				// The bytes are not there after all: post them again next time.
				media.sent = false;
				await put("media", id, media);
				throw error;
			}
			await del("media", id);
		} catch (error) {
			if (error instanceof ApiError && error.code === "attachment_ready") {
				await del("media", id);
				continue;
			}
			done = false;
		}
	}
	return done;
}
